import { Pagination } from 'antd'
import React, { useEffect, useState } from 'react'
import './CustomPagination.scss'
import { useSearchContext } from '../../context/search.context'
import { next, Previous } from '../../Common'

const CustomPagination = ({
  total = 50,
  pageSize = 10,
  handleOnChange
}: {
  total: number
  pageSize: number
  handleOnChange: (page: number) => void
}) => {
  const { page } = useSearchContext()
  // State to track the current page
  const [currentPage, setCurrentPage] = useState(page) // Set the initial active page as 2 (from your HTML)
  const [width, setWidth] = useState<number>(window?.innerWidth)

  const onChange = (page: number) => {
    setCurrentPage(page) // Update the current page on change
    handleOnChange(page) // Call the parent component's page change handler
  }

  function handleWindowSizeChange() {
    setWidth(window?.innerWidth)
  }
  useEffect(() => {
    // Only add event listener if window is available
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleWindowSizeChange)
      return () => {
        window.removeEventListener('resize', handleWindowSizeChange)
      }
    }
  }, [])

  const isMobile = width <= 768

  return (
    <nav className='pager' role='navigation' aria-labelledby='pagination-heading'>
      <h4 id='pagination-heading' className='visually-hidden'>
        Pagination
      </h4>
      {isMobile ? (
        <Pagination
          defaultCurrent={1}
          total={total}
          showLessItems={true}
          size={'default'}
          align={'center'}
          current={currentPage}
          onChange={onChange}
          showSizeChanger={false} // Disable page size change if not needed
          showQuickJumper={false} // Disable quick jump if not needed
          className='pager__items'
          itemRender={(page, type, originalElement) => {
            // Custom rendering for first, last, previous, next, and page items
            const totalPages = Math.ceil(total / pageSize)

            if (type === 'prev') {
              return currentPage > 1 ? (
                <a href='#' title='Go to previous page' rel='prev'>
                  <span className='visually-hidden'>Previous page</span>
                  <i className='icons arrow-left'></i>
                </a>
              ) : null
            }

            if (type === 'next') {
              return currentPage < totalPages ? (
                <a href='#' title='Go to next page' rel='next'>
                  <span className='visually-hidden'>Next page</span>
                  <i className='icons arrow-right'></i>
                </a>
              ) : null
            }

            if (type === 'page') {
              // Conditionally render pages based on currentPage and totalPages
              if (
                (currentPage === 1 && page <= 3) || // Show 1, 2, 3 when current page is 1
                (currentPage === totalPages && page >= totalPages - 2) || // Show last three pages when on the last page
                (page >= currentPage - 1 && page <= currentPage + 1) // Show 3 pages around the current page
              ) {
                return (
                  <a href='#' title={`Go to page ${page}`}>
                    <span className='visually-hidden'>Page</span>
                    {page}
                  </a>
                )
              }
              return null
            }
            return originalElement
          }}
        />
      ) : (
        <Pagination
          current={currentPage} // Set the current active page
          total={total} // Total number of items, adjust according to your logic
          pageSize={pageSize} // Number of items per page (you can adjust as needed)
          onChange={onChange} // Page change handler
          showSizeChanger={false} // Disable page size change if not needed
          showQuickJumper={false} // Disable quick jump if not needed
          itemRender={(page, type, originalElement) => {
            // Custom rendering for first, last, previous, next, and page items
            const totalPages = Math.ceil(total / pageSize)

            if (type === 'prev') {
              return currentPage > 1 ? (
                <a href='#' title='Go to previous page' rel='prev'>
                  <span className='visually-hidden'>Previous page</span>
                  <i className='icons arrow-left'></i>
                  <span aria-hidden='true'>{Previous}</span>
                </a>
              ) : null
            }

            if (type === 'next') {
              return currentPage < totalPages ? (
                <a href='#' title='Go to next page' rel='next'>
                  <span className='visually-hidden'>Next page</span>
                  <span aria-hidden='true'>{next}</span>
                  <i className='icons arrow-right'></i>
                </a>
              ) : null
            }

            if (type === 'page') {
              // Conditionally render pages based on currentPage and totalPages
              if (
                (currentPage === 1 && page <= 3) || // Show 1, 2, 3 when current page is 1
                (currentPage === totalPages && page >= totalPages - 2) || // Show last three pages when on the last page
                (page >= currentPage - 1 && page <= currentPage + 1) // Show 3 pages around the current page
              ) {
                return (
                  <a href='#' title={`Go to page ${page}`}>
                    <span className='visually-hidden'>Page</span>
                    {page}
                  </a>
                )
              }
              return null
            }
            return originalElement
          }}
          className='pager__items'
          rootClassName='pjtest'
        />
      )}
    </nav>
  )
}

export default CustomPagination
